/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchImportLists } from '../../../../../state/thunks/commenting';
// utils
// material
import { ListTable, Group, Button, Title, Icon, ToolTip, Loader, InlineMessage, displayModal, log } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";
// content
import CancelImportPopup from './cancelimportpopup/cancelimportpopup';
import NewImport from './newimport/newimport';
// styles
import injectSheet from 'react-jss';
import styles from './importcomments_styles';


const defaultCols = { 
  importSource: 'Import Source', 
  dateRange: 'Import Duration', 
  authors: 'Authors',
  articles: 'Articles',
  comments: 'Comments',
  status: 'Status',
  action: 'Action'
}

const statusList = ['Pending', 'Prepared', 'Skipped', 'In Process', 'Postponed', 'Retrying', 'Failed', 'Ported']

  const formattedImportSource = {
      'DISQUS_API': 'Disqus - API',
      'DISQUS_XML': 'Disqus - XML',
      'VUUKLE_XML': 'Vuukle - XML',
      'VUUKLE_JSON': 'Vuukle - JSON',
      'OPEN_WEB_CSV': 'Open Web - CSV'
}

/* ========== ~~~~~~~~~~ IMPORT COMMENTS ~~~~~~~~~~ ========== */
const ImportComments = injectSheet(styles)((props) => {

  const dispatch = useDispatch();

  const { importLists } = useSelector(state => state.commenting)
  const { integrationId } = props;

  const [isOpened, setIsOpened] = useState(true);
  const [isNewImportFormOpen, openNewImportForm] = useState(false);
  const [isLoading, loading] = useState(false)
  const [isRefreshing, refresh] = useState(false)

  useEffect(() => {
    return () => {
      openNewImportForm(false)
    }
  },[])

  useEffect(() => {
    handleLoad()
  }, [integrationId])


  const handleToggle = () => {
    setIsOpened(!isOpened)
    openNewImportForm(false)
  }

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleToggle}
      isOpened={isOpened}
      configTitle={'View'}
      cancelTitle={'Close'}
      title='Import Comments'
    />
  );

  const formatDataStatus = type => {
    return (
      <ul>
        {statusList.map(status => <li><span>{status}: </span><span>{ type[status.toLowerCase().replace(/\s/g, '')] }</span></li>)}
        <li><span>Total:</span> <span>{statusList.map(status => type[status.toLowerCase().replace(/\s/g, '')]).reduce((total, num) => total + num, 0)}</span></li>
      </ul>
    )
  }

  const formatDateToLocalTime = (timestamp) => {
    var localDate = new Date(timestamp);
    if(localDate.getTime() < 0) return ''; // return blank if date is invalid

    const year = new Intl.DateTimeFormat("en", { year: "numeric" }).format(localDate);
    const month = new Intl.DateTimeFormat("en", { month: "short" }).format(localDate);
    const day = new Intl.DateTimeFormat("en", { day: "2-digit" }).format(localDate);
    const time = new Intl.DateTimeFormat("default", {
      hour: "numeric",
      minute: "numeric",
      second: "numeric"
    }).format(localDate);

    return `${month} ${day}, ${year} ${time}`;
  };

  const formattedData = () => importLists.data.map(data => {
    const { 
      importStatus: {
        commentingUnitId,
        status,
        log,
        importSource,  // "DISQUS"/"VUUKLE"/"OPEN_WEB"
        importDateRange,
        importStarted,
        etc
      },
      portingStatus: {
        articles,
        authors,
        comments
      }
    } = data

    return {
      importSource: <div className={'importsource'}>{formattedImportSource[importSource]}</div>,
      dateRange: importSource ? (
        <>
        <div className={'daterange'}>{importDateRange}</div>
        <div className={'daterangeinfo'}>{importStarted ? `Import Started: ${formatDateToLocalTime(importStarted)}` : ''}</div>
        <div className={'daterangeinfo'}>{etc ? `Rough ETA: ${formatDateToLocalTime(etc)}` : ''}</div>
        </>
      ) : '', 
      authors: formatDataStatus(authors),
      comments: formatDataStatus(comments),
      articles: formatDataStatus(articles),
      status: <div className={'statuscolumn'}>
        <span className={'status'}>{status}</span> {log.length ? <Icon name={'info-circle'} handleClick={() => showStatusLogsModal(log)}/> : ''}
      </div>,
      action: (
        <div className={'actioncolumn'}>
          <Group name={'actionbtn'}>
            <Button 
              text={'Cancel'} 
              icon={'times'} 
              variant={'system'}
              disabled={status.toLowerCase() !== 'parsing' || status.toLowerCase() !== 'scraping'}  
              handleClick={() => handleCancel({vendor: importSource, commentingUnitId})}
            />
            <Button
              text={'Preview'}
              icon={'search'}
              variant={'system'}
              disabled={articles.ported === 0}
              handleClick={() => handlePreview(commentingUnitId)} 
            />
          </Group>
        </div>
      )
    }
  })

  const showStatusLogsModal = logs => dispatch(displayModal(true, 
    <div className={'logsmodal'}>
      <h4>Import Logs</h4>
      <ul>
        {logs.map(log => <li>{log}</li>)}
      </ul>
    </div>)
  )

  const handlePreview = id => window.open(`/dashboard/commenting/import/preview/${id}`)

  const handleStartImport = () => { 
    openNewImportForm(false)
    handleRefresh()
  }

  const handleLoad = useCallback(async() => {
    if(!integrationId) return;
    loading(true)
    const response = await dispatch(fetchImportLists({integrationId}))
    setTimeout(() => {
      if(response) loading(false)
    },1200)
  }, [dispatch, integrationId])
  
  const handleRefresh = useCallback(async() => {
    refresh(true)
    const response = await dispatch(fetchImportLists({integrationId}))
    setTimeout(() => {
      if(response) refresh(false)
    },1200)
  }, [dispatch, integrationId])

  const handleCancel = ({vendor, id}) => dispatch(displayModal(true, <CancelImportPopup vendor={vendor} id={id} integrationId={integrationId} onCancel={handleLoad} />)) 

  const isImportRunning = importLists.data.filter(data => ['parsing', 'persisting', 'scraping'].includes(data.importStatus.status)).length > 0

  log('is import running', importLists.data, isImportRunning) // debug isImportRunning

  const isRateLimitError = importLists.data.filter(data => data.importStatus.status === 'error' && data.importStatus.error && data.importStatus.error === 'rateLimitError').length > 0

  log('is rate limit error ', importLists.data, isRateLimitError) // debug isImportRunning

  const newImportBtn = <Button text={'New Import'} variant={'primary'} handleClick={() => openNewImportForm(true)} disabled={isImportRunning || isLoading || isRefreshing} /> 

  return (
    <div className={`importcomments ${props.classes.importcomments}`}>
      {isOpened && <>
        <SectionCard>
          <Group name={'imports-header'}>
            <Title><h2>Imports</h2></Title>
            <Group name={'imports-table-action'}>
              { !isNewImportFormOpen && 
                <>
                   <Button 
                    name={'refresh-imports'} 
                    icon={'sync'}
                    text={'Refresh'}
                    processing={isRefreshing}
                    disabled={isRefreshing || isLoading}
                    processingText={'Refreshing...'}
                    showCustomLoaderIcon={true}
                    variant={'system'} 
                    handleClick={handleRefresh}
                  />
                  { isImportRunning ?
                    <ToolTip 
                      trigger={'mouseenter focus'} 
                      content={<div><Icon name={'exclamation-circle'} /> Only 1 import can be run at a time.</div>}
                    >
                      { newImportBtn }
                    </ToolTip>
                  :
                    newImportBtn
                  }
                </>
              }
            </Group>
          </Group>
          { isNewImportFormOpen && 
            <NewImport integrationId={integrationId} onStartImport={handleStartImport} onCancel={() => openNewImportForm(false)} />
          } 
          { isRefreshing || isLoading ? 
            <div className={'loaderwrapper'}><Loader /></div>
            :
            <>
            { isRateLimitError &&
              <InlineMessage 
                name={'ratelimiterror'}
                icon={'exclamation-triangle'} 
                variant={'secondary'} 
                title={'Note'} 
                message={`Rate limit exceeded! Please select a smaller date range and retry`} 
              />
            }
            <ListTable
              name={`imports`}
              size={`full`}
              columnHeads={defaultCols}
              data={formattedData()}
              rowsPerPage={5}
              paginationLocation={`bottom`}
            />
            </>
          }
        </SectionCard>
      </>}
    </div>
  )
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ImportComments